import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  Box,
  Divider,
  LinearProgress,
  Paper,
  Typography,
} from "@material-ui/core";
import SectionHeader from "components/SectionHeader";
import ButtonWithLoading from "components/ButtonWithLoading";
import apiRequest from "utils/apiRequestWithErrorCode";
import GatewayHeaders, { prohibitedName } from "./GatewayHeaders";
import O365Gateway from "./O365Gateway";
import TransportAgent from "./TransportAgent";
import AllowedIpMasks from "./AllowedIpMasks";

import { ResponseOrganization } from "@dashboard-v3/api";
import { Form, Office365Gateway } from "./types";
import { GatewaySecret } from "./GatewaySecret";
import { SmtpAuthUsers } from "./SmtpAuthUsers";

type Status = "LOADING" | "IDLE" | "ERROR_ON_LOADING" | "SAVING";

export default function Gateway() {
  const { t } = useTranslation("gateway");
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState<Form>(defaultState);
  const [office365Gateway, setOffice365Gateway] = useState<Office365Gateway>();
  const [status, setStatus] = useState<Status>("LOADING");

  useEffect(() => {
    async function fetchOrganization() {
      try {
        const organization = await apiRequest<ResponseOrganization>(
          "GET",
          "/organization"
        );
        setStateFromOrganization(organization);
        setOffice365Gateway(organization.office365Gateway);
        setStatus("IDLE");
      } catch (error) {
        console.error(error);
        setStatus("ERROR_ON_LOADING");
      }
    }

    fetchOrganization();
  }, []);

  function setStateFromOrganization(organization) {
    const { gateway } = organization;

    setForm({
      ...defaultState,
      gateway: {
        ...gateway,
        headers: gateway.headers ? headersWithId(gateway.headers) : [],
      },
    });
  }

  const handleGatewayHeaders = newHeaders => {
    setForm(current => {
      const updated = { ...current };
      updated.gateway.headers = newHeaders;
      return updated;
    });
  };

  async function saveChanges(event) {
    event.preventDefault();
    setStatus("SAVING");

    try {
      const organization = await apiRequest("PATCH", "/organization", {
        ...form,
        gateway: {
          ...form.gateway,
          headers: validHeaders(form.gateway.headers),
        },
      });
      setStateFromOrganization(organization);
      enqueueSnackbar(t("savedOk"), { variant: "success" });
    } catch (error) {
      enqueueSnackbar(t("savedFail"), { variant: "error" });
    } finally {
      setStatus("IDLE");
    }
  }

  if (status === "ERROR_ON_LOADING") {
    return (
      <>
        <SectionHeader title="Gateway" />
        <Paper style={{ padding: "12px 16px" }}>
          <Box p={2}>
            <Typography>{t("fetchingOrgError")}</Typography>
          </Box>
        </Paper>
      </>
    );
  }

  if (status === "LOADING") {
    return (
      <>
        <SectionHeader title="Gateway" />
        <Paper style={{ padding: "12px 16px" }}>
          <LinearProgress />
        </Paper>
      </>
    );
  }

  return (
    <>
      <SectionHeader title={t("mainLabel")} />
      <form onSubmit={saveChanges} style={{ padding: 0 }}>
        <Box component={Paper} py={2} px={2}>
          <Box>
            <Typography variant="subtitle2">
              {t("transportAgentLabel")}
            </Typography>
            <Box my={2}>
              <TransportAgent
                gateway={form.gateway}
                updateForm={setForm}
                onNewTransportAgent={org => {
                  setStateFromOrganization(org);
                }}
              />
              <Divider style={{ margin: "25px 0 20px" }} />
              <GatewaySecret
                gateway={form.gateway}
                onNewSecret={org => setStateFromOrganization(org)}
              />
              <Divider style={{ margin: "25px 0 20px" }} />
              <SmtpAuthUsers />
              <Divider style={{ margin: "25px 0 20px" }} />
              <AllowedIpMasks
                value={form.gateway.allowedIpMasks}
                updateForm={setForm}
              />
              <Divider style={{ margin: "25px 0 20px" }} />
              <GatewayHeaders
                headers={form.gateway.headers}
                onChange={handleGatewayHeaders}
              />
              <Divider style={{ margin: "25px 0 20px" }} />
              <O365Gateway gateway={office365Gateway} />
              <Divider style={{ margin: "25px 0 20px" }} />
            </Box>
          </Box>
          <Box mt={3}>
            <ButtonWithLoading
              variant="contained"
              color="primary"
              disableElevation
              onClick={() => {}}
              type="submit"
              loading={status === "SAVING"}
              size="small"
            >
              {t("saveBtnLabel")}
            </ButtonWithLoading>
          </Box>
        </Box>
      </form>
    </>
  );
}

function headersWithId(headers) {
  return headers.map(header => ({
    ...header,
    // id: nextId(),
  }));
}

function validHeaders(headers) {
  const headersWithoutId = headers.map(({ id, ...rest }) => ({
    ...rest,
  }));

  return headersWithoutId.filter(header => !prohibitedName(header.name));
}

const defaultState = () => {
  return {
    gateway: {
      onlyProcessRulesWithTA: false,
      allowedTransportAgent: "",
      allowedIpMasks: [],
      headers: [],
    },
  };
};
